import * as cornerstone from "@cornerstonejs/core";
import { getRenderingEngine } from "@cornerstonejs/core";
import { RequestType } from "@cornerstonejs/core/enums";
import * as cornerstonetools from "@cornerstonejs/tools";
import {
  ToolGroupManager
} from "@cornerstonejs/tools";
import {
  createStackImageSynchronizer,
  createVOISynchronizer,
} from "@cornerstonejs/tools/synchronizers";
import registerWebImageLoader from "./RegisterWebImageLoader";
import dicomImageLoader from "@cornerstonejs/dicom-image-loader";
import { clearDicomOverlayCaches } from "./DicomOverlayPlaneService";
import { stackPrefetch } from "@cornerstonejs/tools/utilities";
import { isMobile } from "react-device-detect";
import { MouseBindings } from "@cornerstonejs/tools/enums";
import SeriesStackScrollMouseWheelTool from "./SeriesStackScrollMouseWheelTool";
import CobbAngleWithDistanceTool from "./CobbAngleWithDistanceTool";
import {
  registerVolumeLoader,
} from "@cornerstonejs/core/loaders/volumeLoader";
import {
  cornerstoneStreamingImageVolumeLoader,
  decimatedVolumeLoader,
} from "@cornerstonejs/core";
import {
  registerGlobalMetadataProvider,
  registerCalibrationMetadataProviders,
} from "./MetadataProviderRegistration";
import { getEnv } from '../../../helpers/EnvHelper';
import { attachWebGLContextLossHandlers } from "./CornerstoneWebGLRecovery";
// OLD: duplicate import — CobbAngleWithDistanceTool is already imported above
//      (line 18). A second `import CobbAngleWithDistanceTool` here caused a
//      Babel "Identifier already declared" parse error that broke the Vite build.
// NEW: commented out the redundant import; the line-18 import is the single
//      source. CalibrationLineTool import is kept.
// import CobbAngleWithDistanceTool from "./CobbAngleWithDistanceTool";
import CalibrationLineTool from "./CalibrationLineTool";
import PRTextTool from "./PRTextTool";
import { registerPTSuvScalingProvider } from "./PTSuvScalingService";

/**
 * Install the PR-aware getTextLines on a tool group's PlanarFreehandROITool,
 * wrapping the tool's OWN default so user-drawn freehand ROIs keep exactly the
 * text they show today:
 *  • data.suppressStats → no text (PR pencil, bug 25; vendor PR shapes — the
 *    method-of-disks lines must not show a recomputed "0.716 cm US Region");
 *  • data.storedLabel  → the PR's stored value verbatim (bug 15);
 *  • otherwise         → the tool default.
 * Cornerstone 4.x only consults configuration.getTextLines when drawing the
 * value, so the flags are inert unless this is installed. Idempotent.
 */
export const installFreehandTextLines = (toolGroup) => {
  const name = cornerstonetools.PlanarFreehandROITool.toolName;
  const instance = toolGroup?.getToolInstance?.(name);
  const current = instance?.configuration?.getTextLines;
  if (!instance || typeof current !== "function" || current.__prAware) return;
  const prAware = (data, targetId) => {
    if (data?.suppressStats) return [];
    if (data?.storedLabel) return [data.storedLabel];
    return current(data, targetId);
  };
  prAware.__prAware = true;
  toolGroup.setToolConfiguration(name, { getTextLines: prAware });
};

const configuration = {
  maxImagesToPrefetch: Infinity,
  preserveExistingPool: true,
};

/**
 * Determines the optimal cache size based on device capabilities.
 * Mobile gets a smaller cache; desktop scales with available memory.
 * Cornerstone v4 (WebGL/GPU rendering) needs more cache than v2.
 */
const getOptimalCacheSize = () => {
  const GB = 1024 * 1024 * 1024;
  if (isMobile) {
    return GB * 1; // 1 GB for mobile
  }
  // Use deviceMemory API if available (Chrome), otherwise default
  const deviceMemoryGB = navigator.deviceMemory || 8;
  // Use ~40% of device RAM for cache, clamped between 3GB and 8GB
  const cacheGB = Math.max(3, Math.min(8, Math.floor(deviceMemoryGB * 0.4)));
  return GB * cacheGB;
};

const isFiniteNumber = (value) => Number.isFinite(value);

const getCircleROICustomTextLines = (data, targetId) => {
  const cachedVolumeStats = data?.cachedStats?.[targetId];

  if (!cachedVolumeStats) {
    return [];
  }

  const {
    radius,
    radiusUnit,
    area,
    mean,
    stdDev,
    max,
    min,
    isEmptyArea,
    areaUnit,
    modalityUnit,
  } = cachedVolumeStats;

  // If pixel stats are missing, ROI is outside image bounds for this target.
  // Hide all text (radius/area/min/max/mean/std dev) in that case.
  const hasPixelStats = [mean, max, min, stdDev].some(isFiniteNumber);
  if (!hasPixelStats) {
    return [];
  }

  const textLines = [];

  if (isFiniteNumber(radius)) {
    const radiusLine = isEmptyArea
      ? "Radius: Oblique not supported"
      : `Radius: ${cornerstone.utilities.roundNumber(radius)} ${radiusUnit}`;
    textLines.push(radiusLine);
  }

  if (isFiniteNumber(area)) {
    const areaLine = isEmptyArea
      ? "Area: Oblique not supported"
      : `Area: ${cornerstone.utilities.roundNumber(area)} ${areaUnit}`;
    textLines.push(areaLine);
  }

  if (isFiniteNumber(mean)) {
    textLines.push(`Mean: ${cornerstone.utilities.roundNumber(mean)} ${modalityUnit}`);
  }

  if (isFiniteNumber(max)) {
    textLines.push(`Max: ${cornerstone.utilities.roundNumber(max)} ${modalityUnit}`);
  }

  if (isFiniteNumber(min)) {
    textLines.push(`Min: ${cornerstone.utilities.roundNumber(min)} ${modalityUnit}`);
  }

  if (isFiniteNumber(stdDev)) {
    textLines.push(`Std Dev: ${cornerstone.utilities.roundNumber(stdDev)} ${modalityUnit}`);
  }

  return textLines;
};

/**
 * Ellipse / Rectangle readout in the Advance viewer's (MedDream) layout:
 *   Mean / SD / Sum
 *   Min / Max
 *   Area: x cm² (W: x cm H: x cm)
 * MedDream manual 8.8.0 p.107: "Ellipse and Rectangle is used to measure area in
 * cm2 units, length and width in cm units, minimum, maximum and medium brightness
 * (marked Mean) ... standard deviation (marked SD) ... and sum value".
 * Intensity unit is whatever Cornerstone reports: HU on CT, SUV on PET once
 * PTSuvScalingService has pre-scaled the image.
 * Cornerstone 4.x already stores min and the voxel count, so no custom stats
 * calculator is needed (the 2.17.6 version of this needed one); Sum = mean × count.
 */
const roiStatsGetTextLines = (shape) => (data, targetId) => {
  if (data?.suppressStats) return [];
  if (data?.storedLabel) return [data.storedLabel];
  const stats = data?.cachedStats?.[targetId];
  if (!stats) return [];
  // 2 decimals, trailing zeros stripped (427.70 → "427.7"), as MedDream shows.
  const fmt = (v) => parseFloat(Number(v).toFixed(2)).toString();
  const { area, mean, stdDev, max, min, isEmptyArea, areaUnit, modalityUnit, statsArray } = stats;

  const count = Array.isArray(statsArray)
    ? statsArray.find((s) => s?.name === "count")?.value
    : undefined;
  const sum = isFiniteNumber(mean) && isFiniteNumber(count) ? mean * count : undefined;

  const u = modalityUnit && String(modalityUnit).trim() ? String(modalityUnit).trim() : "";
  const withU = (v) => (u ? `${fmt(v)} ${u}` : fmt(v));

  // Area mm² → cm² (only when calibrated to mm²; leave px² / other units alone).
  // Strip the calibration-type suffix Cornerstone appends ("mm² CALIBRATED").
  const cleanAreaUnit = (areaUnit || "mm²").split(" ")[0];
  const calibrated = cleanAreaUnit === "mm²";
  const areaVal = isFiniteNumber(area) && calibrated ? area / 100 : area;
  const areaUnitOut = calibrated ? "cm²" : cleanAreaUnit;

  // W/H from the world-space handles (mm → cm). Handle order:
  //   Rectangle [0]=BL [1]=BR [2]=TL [3]=TR · Ellipse [0]=bottom [1]=top [2]=left [3]=right
  let wCm, hCm;
  const pts = data?.handles?.points;
  if (calibrated && Array.isArray(pts) && pts.length >= 4) {
    const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
    const wMm = shape === "ellipse" ? dist(pts[2], pts[3]) : dist(pts[0], pts[1]);
    const hMm = shape === "ellipse" ? dist(pts[0], pts[1]) : dist(pts[0], pts[2]);
    if (isFiniteNumber(wMm)) wCm = wMm / 10;
    if (isFiniteNumber(hMm)) hCm = hMm / 10;
  }

  const lines = [];
  const l1 = [];
  if (isFiniteNumber(mean))   l1.push(`Mean: ${withU(mean)}`);
  if (isFiniteNumber(stdDev)) l1.push(`SD: ${withU(stdDev)}`);
  if (isFiniteNumber(sum))    l1.push(`Sum: ${withU(sum)}`);
  if (l1.length) lines.push(l1.join("  "));

  const l2 = [];
  if (isFiniteNumber(min)) l2.push(`Min: ${withU(min)}`);
  if (isFiniteNumber(max)) l2.push(`Max: ${withU(max)}`);
  if (l2.length) lines.push(l2.join("  "));

  if (isFiniteNumber(areaVal)) {
    if (isEmptyArea) {
      lines.push("Area: Oblique not supported");
    } else {
      let line = `Area: ${fmt(areaVal)} ${areaUnitOut}`;
      if (isFiniteNumber(wCm) && isFiniteNumber(hCm)) line += ` (W: ${fmt(wCm)} cm H: ${fmt(hCm)} cm)`;
      lines.push(line);
    }
  }
  return lines;
};

// --- Rendering mode (CPU vs GPU) ------------------------------------------
// Volume / MPR viewports only work on the GPU (WebGL); Cornerstone has no CPU
// implementation of them. The mobile viewer defaults to the safe CPU path for
// plain 2D, but reconstructable studies (MR/CT/PT/NM) need the GPU so MPR works.
// The viewer sets this flag once the study modality is known; the actual
// setUseCPURendering() call happens right before the rendering engine is created
// (see getRenderingEngineLocal), which is when Cornerstone binds the mode.
let preferGpuRendering = false;
export const setPreferGpuRendering = (value) => {
  preferGpuRendering = !!value;
};

const supportsWebGL2 = () => {
  try {
    const testCanvas = document.createElement("canvas");
    const ok = !!(window.WebGL2RenderingContext && testCanvas.getContext("webgl2"));
     return ok;
  } catch (e) {
    return false;
  }
};

const setSeriesStackScrollConfig = (toolGroupManager, seriesScrollCallback) => {
  const seriesStackScrollConfig = toolGroupManager.getToolConfiguration(SeriesStackScrollMouseWheelTool.toolName);
  const seriesStackScrollMergedConfig = { ...seriesStackScrollConfig, seriesScrollCallback };
  toolGroupManager.setToolConfiguration(SeriesStackScrollMouseWheelTool.toolName,seriesStackScrollMergedConfig, true);
}

// ---- Cornerstone request-pool caps ------------------------------------
// Two SEPARATE singletons need configuring:
//   1. imageLoadPoolManager (alias: requestPoolManager) — wadouri
//      single-frame loads, stack prefetch, generic image loads.
//      Defaults: Interaction=1000, Thumbnail=1000, Prefetch=1000.
//   2. imageRetrievalPoolManager — wadors per-frame loads for un-split
//      multi-frame DICOMs. Defaults: 200 in every lane.
//
// Both pools fan out XHRs in parallel, and BOTH contribute to the
// browser's shared per-origin socket pool (HTTP/1.1 = 6 sockets). If
// only one is capped, the other still blows the budget — surfacing as
// net::ERR_INSUFFICIENT_RESOURCES, especially in the prior viewer or
// during cine when many priors warm their multi-frame stacks
// simultaneously.
//
// Applied UNCONDITIONALLY at module load — not gated behind
// `isCornerstoneInitialized()` because the Gizmo and AdvanceViewer
// entry points also call `cornerstone.init()` without configuring
// these pools, and if they win the race our caps would be skipped.
// `setMaxSimultaneousRequests` is idempotent so calling repeatedly is
// safe.
const applyPoolCaps = () => {
  // Higher caps now that the up-front multi-frame preload paths are
  // disabled (the original ERR_INSUFFICIENT_RESOURCES burst source is
  // gone — see CornerstoneThumbnailService and CornerstoneViewportService).
  // 100 per lane gives plenty of headroom for fast viewport load;
  // the browser will queue anything beyond its per-origin socket
  // limit without errors as long as we don't burst thousands at once.
  //
  // If ERR_INSUFFICIENT_RESOURCES returns, suspect new code that
  // started bulk-enqueueing prefetches — don't lower these caps
  // blindly, fix the burst source instead.
  cornerstone.imageLoadPoolManager.setMaxSimultaneousRequests(RequestType.Interaction, 50);
  cornerstone.imageLoadPoolManager.setMaxSimultaneousRequests(RequestType.Thumbnail, 50);
  cornerstone.imageLoadPoolManager.setMaxSimultaneousRequests(RequestType.Prefetch, 50);

  cornerstone.imageRetrievalPoolManager.setMaxSimultaneousRequests(RequestType.Interaction, 100);
  cornerstone.imageRetrievalPoolManager.setMaxSimultaneousRequests(RequestType.Thumbnail, 100);
  cornerstone.imageRetrievalPoolManager.setMaxSimultaneousRequests(RequestType.Prefetch, 100);

  // console.warn (not log) so it survives a "default" console filter
  // which hides Info-level entries. The "::POOLCAP::" marker is unique
  // enough to grep in a busy console.
};
// Fire at module load — covers every entry point that imports
// CornerstoneService (StudyViewer, SharedViewer, AdvanceViewer).
// Gizmo / AIAnnotatedImage don't import this file, so they also get
// the re-application below when StudyViewer's initialize() later runs.
applyPoolCaps();

export const initialize = async (seriesScrollCallback) => {
  // Re-apply on every initialize() call so the caps stick even if
  // another entry point (Gizmo, AdvanceViewer) called cornerstone.init()
  // earlier and we slipped into the if-already-initialised branch
  // below. Idempotent.
  applyPoolCaps();
  // Also outside the one-time block: the calibratedPixelSpacing providers must
  // be registered even when Gizmo/AdvanceViewer already ran cornerstone.init()
  // and we fall through to the already-initialised branch. Idempotent.
  registerCalibrationMetadataProviders();
  // Pre-scale PT images from raw BQML to SUV so ROI stats read out in SUV.
  // Outside the one-time block for the same reason as above. Idempotent;
  // no-op for non-PET.
  registerPTSuvScalingProvider();
  if (!cornerstone.isCornerstoneInitialized()) {
    try {
      // Interaction requests kept high for responsive scrolling.
      // Thumbnails are now CPU-rendered (no WebGL), so GPU pressure is only
      // from the main viewports — we can afford more aggressive loading.
      cornerstone.imageLoadPoolManager.setMaxSimultaneousRequests(
        RequestType.Interaction,
        100
      );
      cornerstone.imageLoadPoolManager.setMaxSimultaneousRequests(
        RequestType.Thumbnail,
        25
      );
      cornerstone.imageLoadPoolManager.setMaxSimultaneousRequests(
        RequestType.Prefetch,
        15
      );
      cornerstone.imageLoadPoolManager.setMaxSimultaneousRequests(
        RequestType.Compute,
        5
      );

      // Configure image retrieval pool for volume/MPR data streaming
      cornerstone.imageRetrievalPoolManager.setMaxSimultaneousRequests(
        RequestType.Interaction,
        20
      );
      cornerstone.imageRetrievalPoolManager.setMaxSimultaneousRequests(
        RequestType.Thumbnail,
        10
      );
      cornerstone.imageRetrievalPoolManager.setMaxSimultaneousRequests(
        RequestType.Prefetch,
        10
      );

      registerWebImageLoader();

      let maxWebWorkers = 1;

      if (navigator.hardwareConcurrency) {
        maxWebWorkers = Math.min(navigator.hardwareConcurrency, 7);
      }

      // v4 API: only maxWebWorkers and beforeSend are valid options.
      // Loaders (wadouri, dicomweb, dicomfile, wadors) and metadata
      // providers are auto-registered by init() → registerLoaders().
      dicomImageLoader.init({
        maxWebWorkers: maxWebWorkers,
        // ✓ FIX: Now supports async callback for dynamic auth tokens
        beforeSend: async function (xhr, imageId, defaultHeaders, params) {
          xhr.withCredentials = true;
          // Can now add async operations here if needed:
          // const token = await authService.getToken();
          // return { 'Authorization': `Bearer ${token}` };
        },
      });

      // ✓ FIX: Register global metadata provider
      // This prevents duplicate registrations throughout the app
      registerGlobalMetadataProvider();

      // ✓ FIX: Register volume loaders BEFORE cornerstone.init()
      // This prevents race conditions when volumes are requested
      registerVolumeLoader(
        "cornerstoneStreamingImageVolume",
        cornerstoneStreamingImageVolumeLoader
      );
      // Used by loadMPR for series whose full-resolution volume would exceed
      // MAX_MPR_VOLUME_BYTES (see MPRService.jsx) — drops every Nth slice
      // (k-axis decimation) instead of failing to load the volume at all.
      registerVolumeLoader("decimatedStreamingVolume", decimatedVolumeLoader);

      // Pass rendering configuration directly to init() so it deep-merges
      // with defaults (avoids setConfiguration() replacing the entire config)
      cornerstone.init({
        rendering: {
          preferSizeOverAccuracy: true,
          webGlContextCount: 1,
          webgl: {
            preserveDrawingBuffer: false,
          },
          strictZSpacingForVolumeViewport: true,
        },
      });

      if (isMobile) {
        cornerstone.setUseCPURendering(true);
      } else {
        cornerstone.setUseCPURendering(false);
      }

      cornerstonetools.init();
      stackPrefetch.setConfiguration(configuration);
      cornerstone.cache.setMaxCacheSize(getOptimalCacheSize());
      cornerstonetools.ReferenceLinesTool.isAnnotation = false;

      cornerstonetools.addTool(cornerstonetools.StackScrollTool);
      cornerstonetools.addTool(SeriesStackScrollMouseWheelTool);
      cornerstonetools.addTool(cornerstonetools.WindowLevelTool);
      cornerstonetools.addTool(cornerstonetools.ZoomTool);
      cornerstonetools.addTool(cornerstonetools.PanTool);
      cornerstonetools.addTool(cornerstonetools.LengthTool);
      cornerstonetools.addTool(cornerstonetools.BidirectionalTool);
      cornerstonetools.addTool(cornerstonetools.EllipticalROITool);
      cornerstonetools.addTool(cornerstonetools.ProbeTool);
      cornerstonetools.addTool(cornerstonetools.RectangleROITool);
      cornerstonetools.addTool(cornerstonetools.ArrowAnnotateTool);
      cornerstonetools.addTool(cornerstonetools.LabelTool);
      cornerstonetools.addTool(PRTextTool);
      cornerstonetools.addTool(cornerstonetools.ReferenceLinesTool);
      cornerstonetools.addTool(cornerstonetools.CrosshairsTool);
      cornerstonetools.addTool(cornerstonetools.AngleTool);
      // Custom Cobb that also labels the line-to-line distance on the dotted link
      // line. Registered under the built-in "CobbAngle" name so PR save/load and
      // tool-group wiring are unchanged.
      cornerstonetools.addTool(CobbAngleWithDistanceTool);
      cornerstonetools.addTool(cornerstonetools.MagnifyTool);
      cornerstonetools.addTool(cornerstonetools.TrackballRotateTool);
      cornerstonetools.addTool(cornerstonetools.PlanarRotateTool);
      cornerstonetools.addTool(cornerstonetools.PlanarFreehandROITool);
      cornerstonetools.addTool(cornerstonetools.CircleROITool, {
        configuration: {
          getTextLines: getCircleROICustomTextLines,
        },
      });
      // MedDream-style calibration line (LengthTool under a distinct name).
      cornerstonetools.addTool(CalibrationLineTool);

      const toolGroupManager = addToolToToolGroupManager();
      setSeriesStackScrollConfig(toolGroupManager, seriesScrollCallback);
    } catch (error) {
      console.error("✗ Failed to initialize Cornerstone:", error);
      throw new Error(
        `Cornerstone initialization failed: ${error.message}`
      );
    }
  }
};

// Backstop so engine creation can NEVER hang waiting on visibility (window
// minimised/unfocused, prerender, or a visibility event that never arrives).
// During active triage the user focuses tabs well within this, so the
// defer-until-visible optimisation still holds for ctrl+click background tabs;
// a tab left unfocused longer than this builds anyway (harmless). Tunable.
const VISIBILITY_WAIT_TIMEOUT_MS = 15000;

// Resolve immediately if the tab is visible, else when it next becomes visible,
// or after timeoutMs as a hard fallback. Never rejects, never hangs.
const waitUntilVisible = (timeoutMs) => {
  if (typeof document === "undefined" || document.visibilityState === "visible") {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    let done = false;
    let timer = null;
    const finish = () => {
      if (done) return;
      done = true;
      try { document.removeEventListener("visibilitychange", onVis); } catch (_) {}
      if (timer) clearTimeout(timer);
      resolve();
    };
    const onVis = () => {
      if (document.visibilityState === "visible") finish();
    };
    try { document.addEventListener("visibilitychange", onVis); } catch (_) {}
    try { timer = setTimeout(finish, timeoutMs); } catch (_) { finish(); }
  });
};

// In-flight engine-creation promises keyed by engine id. Memoising the creation
// is what makes the deferred (await-based) path SAFE: without it, the await
// between "engine missing?" and "new RenderingEngine()" lets concurrent
// viewport-setup calls each construct a duplicate engine with the same id,
// corrupting the cache → black viewport. With it, all racing callers await the
// SAME engine instance.
const engineCreationPromises = new Map();

export const getRenderingEngineLocal = async (RENDERING_ENGINE_ID) => {
  if (!cornerstone.isCornerstoneInitialized()) {
    await initialize();
  }

  let renderingEngine = getRenderingEngine(RENDERING_ENGINE_ID);

  if (renderingEngine && !renderingEngine.hasBeenDestroyed) {
    attachWebGLContextLossHandlers(renderingEngine);
    return renderingEngine;
  }

  // Engine missing or destroyed — create it exactly once even if many calls
  // race here. The get-then-set below has no await between, so it's atomic;
  // racing callers all await the same memoised creation.
  let creation = engineCreationPromises.get(RENDERING_ENGINE_ID);
  if (!creation) {
    creation = (async () => {
      // Defer the WebGL context until the tab is visible so ctrl+click
      // background tabs don't grab a scarce context (~16/process) until
      // focused; the timeout guarantees we never hang.
      await waitUntilVisible(VISIBILITY_WAIT_TIMEOUT_MS);
      // Bind the rendering mode right before the engine is created. Desktop
      // always uses the GPU. Mobile uses the GPU whenever the device supports
      // WebGL2 (so MPR/volume viewports work); only devices without WebGL2 fall
      // back to CPU.
      // NOTE: modality gating was removed here — the engine is created & cached
      // before the study modality is known, so gating on it raced and left the
      // engine stuck on CPU (preferGpuRendering=true but engineUseCPURendering=true).
      // OLD: const useCPURendering = isMobile && !(preferGpuRendering && supportsWebGL2());
      const useCPURendering = isMobile && !supportsWebGL2();
      cornerstone.setUseCPURendering(useCPURendering);
      return new cornerstone.RenderingEngine(RENDERING_ENGINE_ID);
    })();
    engineCreationPromises.set(RENDERING_ENGINE_ID, creation);
    creation
      .catch(() => {})
      .finally(() => {
        if (engineCreationPromises.get(RENDERING_ENGINE_ID) === creation) {
          engineCreationPromises.delete(RENDERING_ENGINE_ID);
        }
      });
  }
  renderingEngine = await creation;

  // Detect WebGL context loss (browser per-process context-limit exhaustion
  // when many studies are open) and offer a guided reload instead of a dead
  // viewer. Idempotent and best-effort.
  attachWebGLContextLossHandlers(renderingEngine);

  return renderingEngine;
};

export const getAugToolGroup = () => {
  let toolGroupManager = ToolGroupManager.getToolGroup(getEnv("REACT_APP_TOOL_GROUP_ID"));
  return toolGroupManager;
};

export const getToolGroupManager = (TOOL_GROUP_ID) => {
  // cornerstonetools.init();

  let toolGroupManager = ToolGroupManager.getToolGroup(TOOL_GROUP_ID);
  if (toolGroupManager) {
    return toolGroupManager;
  }

  if (!toolGroupManager) {
    toolGroupManager = ToolGroupManager.createToolGroup(TOOL_GROUP_ID);
  }

  return toolGroupManager;
};

export const addToolToToolGroupManager = () => {
  const toolGroup = getToolGroupManager(getEnv("REACT_APP_TOOL_GROUP_ID"));
  //adding tools to toolgroup
  toolGroup.addTool(cornerstonetools.StackScrollTool.toolName);
  toolGroup.addTool(SeriesStackScrollMouseWheelTool.toolName);
  toolGroup.addTool(cornerstonetools.WindowLevelTool.toolName);
  toolGroup.addTool(cornerstonetools.ZoomTool.toolName);
  toolGroup.addTool(cornerstonetools.PanTool.toolName);
  toolGroup.addTool(cornerstonetools.LengthTool.toolName);
  toolGroup.addTool(cornerstonetools.BidirectionalTool.toolName);
  // OLD: Cornerstone default readout (Area mm², Mean/Max/Min/Std Dev).
  // toolGroup.addTool(cornerstonetools.EllipticalROITool.toolName);
  // NEW: Advance-viewer (MedDream) readout — Mean/SD/Sum, Min/Max, Area cm² with W/H.
  toolGroup.addTool(cornerstonetools.EllipticalROITool.toolName, {
    getTextLines: roiStatsGetTextLines("ellipse"),
  });
  toolGroup.addTool(cornerstonetools.ProbeTool.toolName);
  // OLD: toolGroup.addTool(cornerstonetools.RectangleROITool.toolName);
  // NEW: same MedDream readout as Ellipse.
  toolGroup.addTool(cornerstonetools.RectangleROITool.toolName, {
    getTextLines: roiStatsGetTextLines("rect"),
  });
  toolGroup.addTool(cornerstonetools.ArrowAnnotateTool.toolName);
  toolGroup.addTool(cornerstonetools.CrosshairsTool.toolName);
  toolGroup.addTool(cornerstonetools.AngleTool.toolName);
  toolGroup.addTool(cornerstonetools.CobbAngleTool.toolName);
  toolGroup.addTool(cornerstonetools.MagnifyTool.toolName);
  toolGroup.addTool(cornerstonetools.PlanarFreehandROITool.toolName, {
    cachedStats: true,
  });
  installFreehandTextLines(toolGroup);
  toolGroup.addTool(cornerstonetools.CircleROITool.toolName, {
    getTextLines: getCircleROICustomTextLines,
  });
  toolGroup.addTool(CalibrationLineTool.toolName);
  toolGroup.addTool(cornerstonetools.ReferenceLinesTool.toolName);
  // Label: plain text boxes restored from Presentation States (PR text objects,
  // e.g. a measurement list). Annotations only render for tools in the group.
  toolGroup.addTool(cornerstonetools.LabelTool.toolName);
  // PR measurement texts with a copy button (Label + copy icon).
  toolGroup.addTool(PRTextTool.toolName);
  //setting active tool
  toolGroup.setToolActive(SeriesStackScrollMouseWheelTool.toolName, {
    bindings: [
      {
        mouseButton: MouseBindings.Wheel,
      },
    ],
  });

  // //setting disabled tool
  // toolGroup.setToolDisabled(cornerstonetools.ReferenceLinesTool.toolName);

  //setting passive tools
  toolGroup.setToolPassive(cornerstonetools.WindowLevelTool.toolName);
  toolGroup.setToolActive(cornerstonetools.ZoomTool.toolName);
  toolGroup.setToolActive(cornerstonetools.PanTool.toolName);
  toolGroup.setToolPassive(cornerstonetools.LengthTool.toolName);
  toolGroup.setToolPassive(cornerstonetools.BidirectionalTool.toolName);
  toolGroup.setToolPassive(cornerstonetools.EllipticalROITool.toolName);
  toolGroup.setToolPassive(cornerstonetools.ProbeTool.toolName);
  toolGroup.setToolPassive(cornerstonetools.RectangleROITool.toolName);
  toolGroup.setToolPassive(cornerstonetools.ArrowAnnotateTool.toolName);
  toolGroup.setToolPassive(cornerstonetools.AngleTool.toolName);
  toolGroup.setToolPassive(cornerstonetools.CobbAngleTool.toolName);
  toolGroup.setToolPassive(cornerstonetools.MagnifyTool.toolName);
  toolGroup.setToolPassive(cornerstonetools.PlanarFreehandROITool.toolName);
  toolGroup.setToolPassive(cornerstonetools.CircleROITool.toolName);
  toolGroup.setToolPassive(CalibrationLineTool.toolName);
  toolGroup.setToolPassive(cornerstonetools.LabelTool.toolName);
  toolGroup.setToolPassive(PRTextTool.toolName);
  return toolGroup;
};

export const getSynchronizer = (SYNCHRONIZER_ID) => {
  let synchronizer =
    cornerstonetools.SynchronizerManager.getSynchronizer(SYNCHRONIZER_ID);

  if (!synchronizer) {
    synchronizer = createStackImageSynchronizer(SYNCHRONIZER_ID);
  }

  return synchronizer;
};

export const destroySynchronizer = (SYNCHRONIZER_ID) => {
  cornerstonetools.SynchronizerManager.destroySynchronizer(SYNCHRONIZER_ID);
  cornerstonetools.SynchronizerManager.destroy();
};

export const getVOISynchronizer = (SYNCHRONIZER_ID) => {
  let synchronizer =
    cornerstonetools.SynchronizerManager.getSynchronizer(SYNCHRONIZER_ID);

  if (!synchronizer) {
    synchronizer = createVOISynchronizer(SYNCHRONIZER_ID);
  }

  return synchronizer;
};

export const makeBytesAvailableInCache = (requiredBytes) => {
  const MB = 1024 * 1024;
  const bytesNeeded = requiredBytes || MB * 512; // default 512 MB
  const available = cornerstone.cache.getBytesAvailable();
  if (available < bytesNeeded) {
    cornerstone.cache.decacheIfNecessaryUntilBytesAvailable(bytesNeeded);
  }
};

export const purgeCache = () => {
  cornerstone.cache.purgeCache();
  // Overlay planes are keyed by imageId and hold copies of the overlay bits;
  // they are dead the moment their images leave the cache.
  clearDicomOverlayCaches();
};

export const purgeVolumeCache = () => {
  cornerstone.cache.purgeVolumeCache();
};

/**
 * Evicts images from a specific series to free cache space.
 * Call before loading a new series into a viewport that already has one.
 */
export const evictSeriesFromCache = (imageIds) => {
  if (!imageIds || imageIds.length === 0) return;
  for (const imageId of imageIds) {
    try {
      cornerstone.cache.removeImageLoadObject(imageId);
    } catch (_) {
      // image may not be in cache
    }
  }
};

/**
 * Returns current cache utilization info for debugging/monitoring.
 */
export const getCacheStats = () => {
  const maxSize = cornerstone.cache.getMaxCacheSize();
  const currentSize = cornerstone.cache.getCacheSize();
  const available = cornerstone.cache.getBytesAvailable();
  const MB = 1024 * 1024;
  return {
    maxSizeMB: Math.round(maxSize / MB),
    currentSizeMB: Math.round(currentSize / MB),
    availableMB: Math.round(available / MB),
    utilizationPercent: Math.round((currentSize / maxSize) * 100),
  };
};

 

export const getMetadataTagValue = (metadataTag, imageId) => {
  try {
    return cornerstone.metaData.get(metadataTag, imageId);
  } catch (error) {
    console.error('Error fetching metadata:', error);
    return 'Error fetching metadata';
  }
};
